import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, MessageCircle, Star, Facebook, Instagram, Linkedin, Youtube } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_f90ccf59-22af-4309-9fe3-b65d82f9d7de/artifacts/ssdhf0gw_IMG_1394.jpeg";
const PHONE_NUMBER = "+919971231812";
const EMAIL = "info@aggarwalfoampalace.com";
const WHATSAPP_NUMBER = "919971231812";

// Social Media Links (placeholders - update later)
const socialLinks = {
  facebook: "https://facebook.com",
  instagram: "https://instagram.com",
  linkedin: "https://linkedin.com",
  youtube: "https://youtube.com",
  indiamart: "https://www.indiamart.com/aggarwal-foam-palace-new-delhi/"
};

export const Footer = () => {
  return (
    <footer 
      data-testid="footer"
      className="bg-stone-900 text-white"
    >
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          
          {/* Brand Section */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <img 
                src={LOGO_URL} 
                alt="Aggarwal Foam Palace" 
                className="h-14 w-auto rounded-lg"
              />
            </div>
            <p className="text-stone-400 mb-4 leading-relaxed">
              Your trusted destination for premium mattresses, bedding, and home furnishing since 1992.
            </p>
            <div className="flex items-center gap-2 text-amber-500 mb-6">
              <Star className="w-4 h-4 fill-current" />
              <Star className="w-4 h-4 fill-current" />
              <Star className="w-4 h-4 fill-current" />
              <Star className="w-4 h-4 fill-current" />
              <Star className="w-4 h-4 fill-current opacity-50" />
              <span className="text-white ml-2 text-sm">4.5/5 Google Rating</span>
            </div>

            {/* Social Media Icons */}
            <div className="flex items-center gap-3">
              <a 
                href={socialLinks.facebook} 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-9 h-9 bg-stone-800 hover:bg-blue-600 rounded-full flex items-center justify-center transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a 
                href={socialLinks.instagram} 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-9 h-9 bg-stone-800 hover:bg-pink-600 rounded-full flex items-center justify-center transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a 
                href={socialLinks.linkedin} 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-9 h-9 bg-stone-800 hover:bg-blue-700 rounded-full flex items-center justify-center transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-4 h-4" />
              </a>
              <a 
                href={socialLinks.youtube} 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-9 h-9 bg-stone-800 hover:bg-red-600 rounded-full flex items-center justify-center transition-colors"
                aria-label="YouTube"
              >
                <Youtube className="w-4 h-4" />
              </a>
              <a 
                href={socialLinks.indiamart} 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-9 h-9 bg-stone-800 hover:bg-orange-600 rounded-full flex items-center justify-center transition-colors text-xs font-bold"
                aria-label="IndiaMART"
              >
                IM
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-6">Quick Links</h3>
            <ul className="space-y-3">
              {[
                { name: 'Home', path: '/' },
                { name: 'About Us', path: '/about' },
                { name: 'Products', path: '/products' },
                { name: 'Gallery', path: '/gallery' },
                { name: 'Contact Us', path: '/contact' },
              ].map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    data-testid={`footer-link-${link.name.toLowerCase().replace(' ', '-')}`}
                    className="text-stone-400 hover:text-teal-400 transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div>
            <h3 className="text-lg font-semibold mb-6">Our Products</h3>
            <ul className="space-y-3 text-stone-400">
              <li>Sleepwell Mattresses</li>
              <li>King Koil Mattresses</li>
              <li>Kurlon & Springwel</li>
              <li>Bedsheets & Towels</li>
              <li>Pillows & Blankets</li>
              <li>Sofa Foam & Mats</li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-6">Contact Us</h3>
            <ul className="space-y-4">
              <li>
                <a 
                  href={`tel:${PHONE_NUMBER}`}
                  data-testid="footer-phone"
                  className="flex items-start gap-3 text-stone-400 hover:text-teal-400 transition-colors"
                >
                  <Phone className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span>+91 99712 31812</span>
                </a>
              </li>
              <li>
                <a 
                  href={`mailto:${EMAIL}`}
                  data-testid="footer-email"
                  className="flex items-start gap-3 text-stone-400 hover:text-teal-400 transition-colors"
                >
                  <Mail className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span>{EMAIL}</span>
                </a>
              </li>
              <li>
                <a 
                  href={`https://wa.me/${WHATSAPP_NUMBER}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-whatsapp"
                  className="flex items-start gap-3 text-stone-400 hover:text-[#25D366] transition-colors"
                >
                  <MessageCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span>WhatsApp Us</span>
                </a>
              </li>
              <li className="flex items-start gap-3 text-stone-400">
                <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <span>
                  Shop No. 72/5, Yusuf Sarai Market,<br />
                  New Delhi, Delhi 110016
                </span>
              </li>
              <li className="flex items-start gap-3 text-stone-400">
                <Clock className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <span>
                  Open Daily: 10:30 AM - 9:30 PM
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Google Map */}
      <div className="w-full h-64 md:h-80">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3504.4768478396877!2d77.20494969999999!3d28.5609763!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390ce3a502df49df%3A0x9bcce61a3cb5d4de!2sAggarwal%20Foam%20Palace!5e0!3m2!1sen!2sin!4v1703000000000!5m2!1sen!2sin"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Aggarwal Foam Palace Location"
          data-testid="footer-map"
        />
      </div>

      {/* Bottom Bar */}
      <div className="bg-stone-950 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-stone-500 text-sm">
            <p>© 2025 Aggarwal Foam Palace. All rights reserved.</p>
            <p className="text-center">
              GST: 07AAAFA4729N1ZB | Proprietor: Mr. Yoginder Aggarwal
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
